import { useState } from 'react';
import { apiClient } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const MUSCLE_GROUPS = [
  'Pectoraux', 'Dos', 'Epaules', 'Bras', 'Jambes', 'Fessiers', 
  'Abdos', 'Full body', 'Cardio', 'Hanches', 'Autre'
];

const UNITS = [
  { value: 'kg', label: 'Kg (charge)' },
  { value: 'reps', label: 'Repetitions' },
  { value: 'sec', label: 'Secondes' },
  { value: 'min', label: 'Minutes' },
  { value: 'm', label: 'Metres' },
  { value: 'km', label: 'Kilometres' },
  { value: 'cal', label: 'Calories' },
  { value: 'tours', label: 'Tours' },
  { value: 'etages', label: 'Etages' },
];

const CATEGORIES = [
  { value: 'machine', label: 'Machine' },
  { value: 'poids_libres', label: 'Poids libres' },
  { value: 'poids_du_corps', label: 'Poids du corps' },
  { value: 'cardio', label: 'Cardio' },
  { value: 'fonctionnel', label: 'Fonctionnel / CrossFit' },
  { value: 'halterophilie', label: 'Halterophilie' },
  { value: 'abdos_core', label: 'Abdos / Core' },
  { value: 'stretching', label: 'Stretching / Mobilite' },
  { value: 'sport', label: 'Sport / Plein air' },
];

export default function CustomExerciseModal({ isOpen, onClose, onCreated, initialName = '' }) {
  const [name, setName] = useState(initialName);
  const [muscle, setMuscle] = useState('Autre');
  const [unit, setUnit] = useState('reps');
  const [category, setCategory] = useState('poids_libres');
  const [shared, setShared] = useState(true);
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      const res = await apiClient.post('/exercises/custom', { name: name.trim(), muscle, unit, category, shared });
      toast.success(`Exercice "${name}" cree !`);
      onCreated?.(res.data);
      onClose();
      setName(''); setMuscle('Autre'); setUnit('reps');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" data-testid="custom-exercise-modal">
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg w-full max-w-md p-5 space-y-4 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#FF6B35]" /> Exercice personnalise
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="close-custom-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <Label className="text-zinc-400 text-xs">Nom de l'exercice *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Curl concentre assis..."
              className="bg-[#0F0F0F] border-[#2A2A2E] text-white text-sm"
              autoFocus
              data-testid="custom-ex-name"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-zinc-400 text-xs">Muscle cible</Label>
            <div className="flex flex-wrap gap-1.5">
              {MUSCLE_GROUPS.map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMuscle(m)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                    muscle === m ? 'bg-[#FF6B35] text-black' : 'bg-[#0F0F0F] text-zinc-400 border border-[#2A2A2E]'
                  }`}
                  data-testid={`muscle-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs">Unite de mesure</Label>
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="w-full bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-sm px-3 py-2 text-sm"
                data-testid="custom-ex-unit"
              >
                {UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs">Categorie</Label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-sm px-3 py-2 text-sm"
                data-testid="custom-ex-category"
              >
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={shared}
              onChange={(e) => setShared(e.target.checked)}
              className="rounded border-zinc-600"
              id="share-exercise"
              data-testid="custom-ex-shared"
            />
            <label htmlFor="share-exercise" className="text-xs text-zinc-400">
              Partager avec le groupe (visible par tous)
            </label>
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-zinc-700 text-zinc-400">
              Annuler
            </Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-[#FF6B35] text-black hover:bg-[#E85A25]" data-testid="save-custom-exercise">
              {saving ? 'Creation...' : 'Creer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
